// src/services/liveMatchEventNormalizer.js
// Backend farklı response formatları dönebilir (yeni ve eski). Bu servis
// hepsini tek bir normalize edilmiş şekle indirger ve maç olayları için
// duplicate/merge yardımcıları sağlar.
//
// Not: Frontend'in mevcut olay modeli (LiveMatchControl) alanları:
//   { id, type, team, playerId, playerName, secondaryPlayerId,
//     secondaryPlayerName, time, minute, clockMs, createdAt }
// Bu servis backend alan adlarını (snake_case / farklı isim) bu modele çevirir
// ve client_event_id / serverId bilgisini korur.

const firstDefined = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

const toInt = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? Math.trunc(num) : fallback;
};

const pad2 = (n) => String(Math.max(0, Number(n) || 0)).padStart(2, "0");

export const formatClockFromMs = (ms) => {
  const total = Math.floor(Math.max(0, Number(ms) || 0) / 1000);
  return `${pad2(Math.floor(total / 60))}:${pad2(total % 60)}`;
};

const normalizeTeamKey = (value) => {
  const str = String(value || "").toLowerCase();
  if (str === "away" || str === "second" || str === "deplasman") return "away";
  return "home";
};

/**
 * Tek bir maç olayını (backend ya da yerel) tek tip modele çevirir.
 * client_event_id ve serverId (backend'in kalıcı id'si) ayrı korunur.
 * @param {object} raw
 * @returns {object|null}
 */
export function normalizeEvent(raw) {
  if (!raw || typeof raw !== "object") return null;

  const clientEventId = firstDefined(
    raw.clientEventId,
    raw.client_event_id,
    raw.clientActionId,
    raw.client_action_id
  );

  const serverId = firstDefined(
    raw.eventId,
    raw.event_id,
    raw.serverId,
    raw.server_id,
    // eski format: sadece "id" numaraysa server id kabul edilir
    typeof raw.id === "number" ? raw.id : undefined
  );

  const clockMs = toInt(firstDefined(raw.clockMs, raw.clock_ms, 0), 0);
  const minute = firstDefined(raw.minute, raw.dakika);

  const normalized = {
    // Tekilleştirmede öncelik: serverId > clientEventId > mevcut id.
    id: firstDefined(serverId, clientEventId, raw.id) ?? null,
    serverId: serverId ?? null,
    clientEventId: clientEventId ?? null,
    type: String(firstDefined(raw.type, raw.event_type, raw.olay_tipi, "goal")),
    team: normalizeTeamKey(firstDefined(raw.team, raw.taraf)),
    playerId: firstDefined(raw.playerId, raw.player_id, raw.oyuncu_id) ?? null,
    playerName:
      firstDefined(raw.playerName, raw.player_name, raw.player, raw.oyuncu) ??
      "",
    secondaryPlayerId:
      firstDefined(
        raw.secondaryPlayerId,
        raw.secondary_player_id,
        raw.assistPlayerId,
        raw.assist_player_id
      ) ?? null,
    secondaryPlayerName:
      firstDefined(
        raw.secondaryPlayerName,
        raw.secondary_player_name,
        raw.assistPlayerName,
        raw.assist_player_name
      ) ?? "",
    time: firstDefined(raw.time, raw.clockText, raw.clock_text) ?? formatClockFromMs(clockMs),
    minute: minute != null ? toInt(minute) : Math.ceil(clockMs / 60000) || 0,
    clockMs,
    period: firstDefined(raw.period, raw.devre) ?? null,
    createdAt: firstDefined(raw.createdAt, raw.created_at) ?? null,
    updatedAt: firstDefined(raw.updatedAt, raw.updated_at) ?? null,
    // pending/confirmed/failed görünüm durumu (optimistic UI için).
    status: raw.status || "confirmed",
  };

  return normalized;
}

/**
 * İki olayın "aynı" olup olmadığını belirler.
 * Öncelik: serverId eşleşmesi, sonra clientEventId eşleşmesi, sonra id.
 */
export function isSameEvent(a, b) {
  if (!a || !b) return false;
  if (a.serverId != null && b.serverId != null) {
    return String(a.serverId) === String(b.serverId);
  }
  if (a.clientEventId && b.clientEventId) {
    return String(a.clientEventId) === String(b.clientEventId);
  }
  if (a.id != null && b.id != null) {
    return String(a.id) === String(b.id);
  }
  return false;
}

/**
 * Bir olay listesine yeni/remote bir olayı, duplicate oluşturmadan ekler
 * veya var olanı günceller. Socket ve API response'un aynı olayı iki kere
 * eklememesi bu fonksiyonla sağlanır.
 *
 * @param {Array} list mevcut olaylar
 * @param {object} incoming normalize edilmiş olay
 * @returns {Array} yeni liste (referans değişir)
 */
export function upsertEvent(list, incoming) {
  const safeList = Array.isArray(list) ? list : [];
  const normalized = incoming?.serverId !== undefined ? incoming : normalizeEvent(incoming);
  if (!normalized) return safeList;

  const index = safeList.findIndex((item) => isSameEvent(item, normalized));

  if (index === -1) {
    return [normalized, ...safeList];
  }

  const existing = safeList[index];

  // Daha yeni updatedAt varsa eski mesaj uygulanmaz (out-of-order koruması).
  if (
    existing.updatedAt &&
    normalized.updatedAt &&
    new Date(normalized.updatedAt).getTime() <
      new Date(existing.updatedAt).getTime()
  ) {
    return safeList;
  }

  const next = safeList.slice();
  // Pending olayı confirmed hale getirirken serverId'yi kazandır,
  // clientEventId'yi koru.
  next[index] = {
    ...existing,
    ...normalized,
    clientEventId: normalized.clientEventId || existing.clientEventId,
    serverId: normalized.serverId ?? existing.serverId,
  };
  return next;
}

/**
 * Bir olayı listeden çıkarır (silme / rollback için).
 */
export function removeEvent(list, matcher) {
  const safeList = Array.isArray(list) ? list : [];
  return safeList.filter((item) => !isSameEvent(item, matcher));
}

/**
 * Backend'in create/update/delete response'unu tek tip normalize eder.
 * Hem yeni format hem eski format desteklenir.
 *
 * Yeni format örneği:
 * { success, created, duplicate, eventId, clientEventId, score, statsStatus }
 * Eski format örneği:
 * { message, olay: {...}, mac: {...} }
 *
 * @param {any} data axios response.data
 * @returns {{
 *   ok: boolean,
 *   created: boolean,
 *   duplicate: boolean,
 *   event: object|null,
 *   score: {home:number|null, away:number|null}|null,
 *   statsStatus: string|null,
 *   raw: any
 * }}
 */
export function normalizeMutationResponse(data) {
  const raw = data ?? null;

  if (!raw || typeof raw !== "object") {
    return {
      ok: true,
      created: true,
      duplicate: false,
      event: null,
      score: null,
      statsStatus: null,
      raw,
    };
  }

  const duplicate = Boolean(raw.duplicate || raw.already_exists);
  const created = raw.created !== undefined ? Boolean(raw.created) : !duplicate;
  const ok = raw.success !== undefined ? Boolean(raw.success) : true;

  // Olay gövdesi çeşitli isimlerle gelebilir.
  const eventBody = firstDefined(raw.event, raw.olay, raw.data);
  const event = eventBody
    ? normalizeEvent({
        ...eventBody,
        eventId: firstDefined(raw.eventId, raw.event_id, eventBody.id),
        clientEventId: firstDefined(
          raw.clientEventId,
          raw.client_event_id,
          eventBody.clientEventId,
          eventBody.client_event_id
        ),
      })
    : raw.eventId != null || raw.event_id != null
    ? normalizeEvent({
        eventId: firstDefined(raw.eventId, raw.event_id),
        clientEventId: firstDefined(raw.clientEventId, raw.client_event_id),
      })
    : null;

  // Skor çeşitli formatlarda gelebilir.
  let score = null;
  const scoreSource = firstDefined(raw.score, raw.skor, raw.mac);
  if (scoreSource && typeof scoreSource === "object") {
    const home = firstDefined(
      scoreSource.home,
      scoreSource.homeScore,
      scoreSource.first_team_score
    );
    const away = firstDefined(
      scoreSource.away,
      scoreSource.awayScore,
      scoreSource.second_team_score
    );
    if (home !== undefined || away !== undefined) {
      score = {
        home: home !== undefined ? toInt(home) : null,
        away: away !== undefined ? toInt(away) : null,
      };
    }
  }

  const statsStatus =
    firstDefined(raw.statsStatus, raw.stats_status) ?? null;

  return {
    ok: ok && !(!created && !duplicate && raw.success === false),
    created,
    duplicate,
    event,
    score,
    statsStatus,
    raw,
  };
}

const liveMatchEventNormalizer = {
  normalizeEvent,
  upsertEvent,
  removeEvent,
  isSameEvent,
  normalizeMutationResponse,
  formatClockFromMs,
};

export default liveMatchEventNormalizer;
